import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, Clock, Coins, Truck } from 'lucide-react';
import ProductCardHybrid from './ProductCardHybrid';
import GlobalHeader from './GlobalHeader';
import '../styles/ProductCardHybrid.css';
import '../styles/HomePage.css';

// Category icons (url: prefix for Parcel 2)
import catShrimp from 'url:../assets/icons/cat-shrimp.webp';
import catCrab from 'url:../assets/icons/cat-crab.webp';
import catFish from 'url:../assets/icons/cat-fish.webp';
import catCaviar from 'url:../assets/icons/cat-caviar.webp';
import catMollusk from 'url:../assets/icons/cat-mollusk.webp';

/**
 * Гибридный каталог с прямым входом (no district selection)
 *
 * Особенности:
 * - Direct-to-catalog UX (0 кликов до товаров)
 * - Сетка товаров с фильтрацией по категориям через верхние табы
 * - Встроенный маркетинг (прогресс, social proof, micro-rewards)
 * - Готово к Telegram Web App + standalone PWA
 */
// Тиры доставки (синхронизированы с cart.jsx)
const DELIVERY_TIERS = [
  { minSum: 0, fee: 300 },
  { minSum: 3000, fee: 200 },
  { minSum: 7000, fee: 100 },
  { minSum: 10000, fee: 0 }
];

// Следующий тир доставки
const getNextDeliveryTier = (orderSum) => {
  for (const tier of DELIVERY_TIERS) {
    if (orderSum < tier.minSum) return tier;
  }
  return null; // уже бесплатная доставка
};

// Текущая стоимость доставки
const getCurrentDeliveryFee = (orderSum) => {
  for (let i = DELIVERY_TIERS.length - 1; i >= 0; i--) {
    if (orderSum >= DELIVERY_TIERS[i].minSum) return DELIVERY_TIERS[i].fee;
  }
  return DELIVERY_TIERS[0].fee;
};

const CatalogHybrid = ({
  progress: progressProp,
  cycleInfo,
  onAddToCart,
  onViewDetails,
  // Для desktop навигации в GlobalHeader
  currentPage = 'catalog',
  onNavigate,
  cartItemsCount = 0,
  cartTotal = 0,
  cartItems = [],
  onUpdateQuantity,
  onRemoveFromCart
}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isBannerVisible, setIsBannerVisible] = useState(true);
  const bannerSentinelRef = useRef(null);

  // Прогресс получаем из App.jsx через prop
  const progress = progressProp || { percent: 0, families: 0 };

  // Дата закрытия цикла: из API или фолбек +7 дней
  // useMemo чтобы не создавать новый Date на каждом рендере (иначе бесконечный цикл useEffect)
  const closeDate = useMemo(() =>
    cycleInfo?.end_date
      ? new Date(cycleInfo.end_date)
      : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    [cycleInfo?.end_date]
  );

  // Countdown: вычисляем сразу, без лишнего setState
  const calcTimeLeft = (end) => {
    const distance = new Date(end).getTime() - Date.now();
    if (distance < 0) return { days: 0, hours: 0, minutes: 0 };
    return {
      days: Math.floor(distance / (1000 * 60 * 60 * 24)),
      hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
    };
  };

  const [timeLeft, setTimeLeft] = useState(() => calcTimeLeft(closeDate));

  // Обновляем таймер раз в минуту
  useEffect(() => {
    setTimeLeft(calcTimeLeft(closeDate));
    const timer = setInterval(() => setTimeLeft(calcTimeLeft(closeDate)), 60000);
    return () => clearInterval(timer);
  }, [closeDate]);

  // IntersectionObserver: когда баннер уходит за sticky header — таймер переезжает в хедер
  // Зависимость от loading — баннер появляется только после загрузки
  useEffect(() => {
    if (!bannerSentinelRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsBannerVisible(entry.isIntersecting),
      { threshold: 0, rootMargin: '-60px 0px 0px 0px' }
    );

    observer.observe(bannerSentinelRef.current);
    return () => observer.disconnect();
  }, [loading]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const productsResponse = await fetch(`/api/products`);
      if (!productsResponse.ok) {
        throw new Error(`HTTP ${productsResponse.status}`);
      }
      const productsJson = await productsResponse.json();
      const productsData = productsJson.products || productsJson || [];

      // Трансформация товаров для гибридного компонента
      const transformedProducts = productsData.map(p => ({
        ...p, // Сохраняем все оригинальные поля (wholesale_pack_size, share_interest_count, cycle_product_id и др.)
        id: p.product_id,
        name: p.product_name, // API возвращает product_name
        product_name: p.product_name,
        shortDescription: p.description_ru?.substring(0, 100) || 'Свежие морепродукты',
        imageUrl: p.image_url,
        category: getCategoryFromProduct(p),
        badge: getBadgeFromProduct(p),
        basePricePerKg: p.price || p.finalPrice || 1000, // API возвращает price
        // Старую (перечёркнутую) цену показываем ТОЛЬКО если API реально её вернул
        oldPricePerKg: (p.old_price ?? p.oldPrice) ? parseFloat(p.old_price ?? p.oldPrice) : null,
        weights: [
          { value: 0.5, label: '500г', isWholesale: false },
          { value: 1, label: '1кг', isWholesale: true },
          { value: 2, label: '2кг', isWholesale: true }
        ],
        // Реальная скидка за упаковку из API (или 0 — карточка не покажет бейдж)
        packagingBonus: p.packaging_bonus_percent ?? p.wholesale_bonus_percent ?? p.packaging_bonus ?? 0,
        buyersCount: p.orders_count ?? 0,
        buyersInitials: p.buyer_initials || []
      }));

      setProducts(transformedProducts);
      setLoading(false);
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
      console.error('Error stack:', error.stack);
      console.error('Error message:', error.message);
      setError('Не удалось загрузить каталог. Попробуйте обновить страницу.');
      setLoading(false);
    }
  };

  // Фильтрация товаров по поисковому запросу и категории
  const filteredProducts = products.filter(p => {
    // Фильтрация по поиску
    const matchesSearch = !searchQuery ||
      p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.shortDescription?.toLowerCase().includes(searchQuery.toLowerCase());

    // Фильтрация по категории
    let matchesCategory = true;
    if (selectedCategory !== 'all') {
      matchesCategory = p.category === selectedCategory;
    }

    return matchesSearch && matchesCategory;
  });

  // Категории с товарами (для показа/скрытия пиллов — без учёта поиска)
  const availableCategories = new Set(products.map(p => p.category));

  // Сброс выбранной категории, если она больше не доступна
  useEffect(() => {
    if (selectedCategory !== 'all' && !availableCategories.has(selectedCategory)) {
      setSelectedCategory('all');
    }
  }, [products]);

  // filteredProducts уже содержит товары с учётом поиска и выбранной категории

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gray-50 dark:bg-gray-900">
        <div className="text-center animate-fadeIn">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-gray-700 dark:text-gray-300 text-base mt-4">
            Загрузка каталога...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="catalog-hybrid">
      {/* Глобальный header — таймер появляется когда баннер уходит за хедер */}
      <GlobalHeader
        location="Москва и МО"
        currentPage={currentPage}
        onNavigate={onNavigate}
        cartItemsCount={cartItemsCount}
        showTimer={!isBannerVisible}
        closeDate={closeDate}
      />

      {/* Ошибка загрузки каталога */}
      {error && (
        <div style={{ padding: '80px 16px 16px', maxWidth: '480px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary, #374151)', marginBottom: '6px' }}>
            {error}
          </div>
          <button
            onClick={() => loadData()}
            style={{
              marginTop: '16px',
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #0EA5E9, #38BDF8)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Обновить
          </button>
        </div>
      )}

      {/* Нет активного цикла — заглушка */}
      {!error && (!cycleInfo || cycleInfo.status === 'inactive') && (
        <div style={{ padding: '80px 16px 16px', maxWidth: '480px', margin: '0 auto', textAlign: 'center' }}>
          <img
            src={new URL('../assets/fishing-boat.png', import.meta.url).href}
            alt="Ушли на поиски лучших даров моря"
            style={{ maxWidth: '180px', width: '100%', margin: '0 auto 12px', display: 'block' }}
          />
          <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary, #374151)', marginBottom: '6px' }}>
            Ушли на поиски лучших даров моря
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary, #6b7280)', lineHeight: 1.5 }}>
            Скоро откроем новый сбор!
          </div>
        </div>
      )}

      {/* Каталог (только при активном цикле) */}
      {!error && cycleInfo?.status && cycleInfo.status !== 'inactive' && <div className="catalog-content">
        {/* Search Bar */}
        <div className="catalog-search-section">
          <div className="catalog-search-bar">
            <Search className="search-icon" size={18} strokeWidth={2} />
            <input
              type="text"
              placeholder="Найти морепродукты..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="search-clear-btn"
                aria-label="Очистить поиск"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Category Pills */}
        <div className="category-section">
          <div className="category-pills">
            <button
              className={`category-pill ${selectedCategory === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('all')}
            >
              Все
            </button>
            {availableCategories.has('shrimp') && (
              <button
                className={`category-pill ${selectedCategory === 'shrimp' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('shrimp')}
              >
                <img src={catShrimp} alt="" className="pill-icon" /> Креветки
              </button>
            )}
            {availableCategories.has('crab') && (
              <button
                className={`category-pill ${selectedCategory === 'crab' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('crab')}
              >
                <img src={catCrab} alt="" className="pill-icon" /> Крабы
              </button>
            )}
            {availableCategories.has('fish') && (
              <button
                className={`category-pill ${selectedCategory === 'fish' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('fish')}
              >
                <img src={catFish} alt="" className="pill-icon" /> Рыба
              </button>
            )}
            {availableCategories.has('caviar') && (
              <button
                className={`category-pill ${selectedCategory === 'caviar' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('caviar')}
              >
                <img src={catCaviar} alt="" className="pill-icon" /> Икра
              </button>
            )}
            {availableCategories.has('mollusk') && (
              <button
                className={`category-pill ${selectedCategory === 'mollusk' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('mollusk')}
              >
                <img src={catMollusk} alt="" className="pill-icon" /> Моллюски
              </button>
            )}
          </div>
        </div>
        {/* Мини-баннер: таймер + интерактивные привилегии */}
        <div className="catalog-promo-banner" ref={bannerSentinelRef}>
          <div className="promo-banner-content">
            <div className="promo-timer">
              <Clock size={13} strokeWidth={2.5} />
              <span>
                {timeLeft.days > 0 && `${timeLeft.days}д `}
                {timeLeft.hours}ч {timeLeft.minutes.toString().padStart(2, '0')}м
              </span>
            </div>
            <span className="promo-separator" />
            <PromoPerks cartTotal={cartTotal} />
          </div>
        </div>

        {/* Нет результатов поиска */}
        {filteredProducts.length === 0 && searchQuery && (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: '#64748b'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
            <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#1e293b' }}>
              Ничего не найдено
            </div>
            <div style={{ fontSize: '14px', marginBottom: '20px' }}>
              По запросу "{searchQuery}" ничего не найдено
            </div>
            <button
              onClick={() => setSearchQuery('')}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #0EA5E9, #38BDF8)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Очистить поиск
            </button>
          </div>
        )}

        {/* Placeholder для пустого состояния (нет товаров в БД) */}
        {products.length === 0 && !searchQuery && (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: '#666'
          }}>
            <img src={new URL('../assets/icons/fish-logo.png', import.meta.url).href} alt="Путь рыбы" style={{ width: '64px', height: '64px', objectFit: 'contain', margin: '0 auto 16px' }} />
            <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Товары скоро появятся
            </div>
            <div style={{ fontSize: '14px', color: '#999' }}>
              Дождитесь начала следующего цикла
            </div>
          </div>
        )}

        {/* Сетка товаров */}
        {filteredProducts.length > 0 && (
          <div className="catalog-products-grid">
            {filteredProducts.map((product) => {
              const cartItem = cartItems.find(ci => ci.id === product.id);
              return (
                <ProductCardHybrid
                  key={product.id}
                  product={product}
                  onAddToCart={onAddToCart}
                  onUpdateQuantity={onUpdateQuantity}
                  onRemoveFromCart={onRemoveFromCart}
                  cartQuantity={cartItem?.quantity || 0}
                  onViewDetails={onViewDetails}
                  gridMode
                />
              );
            })}
          </div>
        )}
      </div>}
    </div>
  );
};

/**
 * PromoPerks — интерактивная часть баннера.
 * Реагирует на сумму корзины и показывает ближайшую цель.
 */
const PromoPerks = ({ cartTotal }) => {
  if (cartTotal === 0) {
    return (
      <span className="promo-perks" style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <Coins size={13} strokeWidth={2} style={{ opacity: 0.8 }} />
          <span>Скидка до 5%</span>
        </span>
        <span style={{ opacity: 0.4 }}>·</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <Truck size={13} strokeWidth={2} style={{ opacity: 0.8 }} />
          <span>Бесплатная доставка от 10 000₽</span>
        </span>
      </span>
    );
  }

  const currentFee = getCurrentDeliveryFee(cartTotal);
  const nextTier = getNextDeliveryTier(cartTotal);

  if (!nextTier) {
    // Уже бесплатная доставка
    return (
      <span className="promo-perks promo-perks--success" style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <Truck size={13} strokeWidth={2} />
          <span>Доставка бесплатно</span>
        </span>
        <span style={{ opacity: 0.4 }}>·</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <Coins size={13} strokeWidth={2} />
          <span>Скидка до 5%</span>
        </span>
      </span>
    );
  }

  const remaining = nextTier.minSum - cartTotal;
  const remainingFormatted = remaining.toLocaleString('ru-RU');

  if (nextTier.fee === 0) {
    // Следующий тир — бесплатная доставка
    return (
      <span className="promo-perks promo-perks--progress" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        <Truck size={13} strokeWidth={2} />
        Ещё {remainingFormatted}₽ до бесплатной доставки
      </span>
    );
  }

  // Следующий тир — дешевле доставка
  return (
    <span className="promo-perks promo-perks--progress" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      <Truck size={13} strokeWidth={2} />
      Ещё {remainingFormatted}₽ — доставка {nextTier.fee}₽ вместо {currentFee}₽
    </span>
  );
};

// Утилиты

function getCategoryFromProduct(product) {
  const name = (product.product_name || product.name || '').toLowerCase();
  if (name.includes('креветк')) return 'shrimp';
  if (name.includes('краб')) return 'crab';
  if (name.includes('рыб') || name.includes('палтус') || name.includes('лосос') || name.includes('тунец') || name.includes('форел') || name.includes('горбуш') || name.includes('нерк') || name.includes('кижуч') || name.includes('стейк')) return 'fish';
  if (name.includes('икра')) return 'caviar';
  if (name.includes('гребеш') || name.includes('устриц') || name.includes('миди') || name.includes('кальмар') || name.includes('осьминог')) return 'mollusk';
  return 'other';
}

// Бейдж выбирается ТОЛЬКО по реальному признаку товара из API.
// Если признаков нет — бейдж не показываем (null).
function getBadgeFromProduct(product) {
  if (product?.badge) return product.badge;
  if (product?.is_hit) return 'ХИТ';
  if (product?.is_new) return 'НОВИНКА';
  if (product?.is_premium) return 'ПРЕМИУМ';
  return null;
}

export default CatalogHybrid;
